import React from 'react';
import { Clock, Calendar, ClipboardList, AlertCircle, CheckCircle2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '../../stores/authStore';

const AssessmentDocStartScreen = ({ document: doc, submission, onStart, isLoading }) => {
  const { userData } = useAuthStore();
  const assessment = doc?.assessment;
  if (!assessment) return null;

  const questionCount = assessment.responseAreaOrder?.length || 0;
  const totalPoints = Object.values(assessment.responseAreas || {}).reduce((sum, ra) => sum + (ra.points || 0), 0);
  const categoryLabel = assessment.category === 'informal'
    ? (assessment.informalType === 'self-practice' ? 'Informal • Self Practice' : 'Informal • Class Practice')
    : 'Formal Assessment';
  const requiresGrading = assessment.category === 'formal' || assessment.informalType === 'class-practice';
  const assignedTeacher = userData?.teacherAssignment || null;
  const assignedTutor = userData?.tutorAssignment || null;

  // Validity checks
  const now = new Date();
  const validFrom = assessment.validFrom ? new Date(assessment.validFrom) : null;
  const validUntil = assessment.validUntil ? new Date(assessment.validUntil) : null;
  const notYetAvailable = validFrom && now < validFrom;
  const expired = validUntil && now > validUntil;
  const alreadySubmitted = submission?.status === 'submitted' || submission?.status === 'timed-out';

  const canStart = !notYetAvailable && !expired && !alreadySubmitted;

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-sigma-800 px-6 py-5 text-white">
          <h1 className="text-xl font-bold">{doc.name || 'Assessment'}</h1>
          <p className="text-sigma-200 text-sm mt-1">{categoryLabel}</p>
        </div>

        {/* Info cards */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <ClipboardList className="w-5 h-5 text-sigma-600 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Questions</div>
                <div className="text-sm font-semibold text-gray-900">{questionCount}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <ClipboardList className="w-5 h-5 text-sigma-600 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Total Points</div>
                <div className="text-sm font-semibold text-gray-900">{totalPoints}</div>
              </div>
            </div>
          </div>

          {assessment.timeLimit && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <div className="text-xs text-amber-700">Time Limit</div>
                <div className="text-sm font-semibold text-amber-900">{assessment.timeLimit} minutes</div>
              </div>
            </div>
          )}

          {requiresGrading && (
            <div className="rounded-lg border border-sigma-100 bg-sigma-50 px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-sigma-700">Submission Route</div>
              <div className="mt-1 text-sm text-sigma-900">
                {assignedTeacher?.displayName || assignedTutor?.displayName ? (
                  <>
                    This assessment will be submitted for grading to{' '}
                    <span className="font-semibold">
                      {assignedTeacher?.displayName || assignedTutor?.displayName}
                    </span>
                    {assignedTeacher?.displayName && assignedTutor?.displayName && assignedTutor?.displayName !== assignedTeacher?.displayName
                      ? ` with ${assignedTutor.displayName} also linked as tutor`
                      : ''}.
                  </>
                ) : (
                  'This assessment requires grading. Add a teacher or tutor in your profile so submissions route correctly.'
                )}
              </div>
            </div>
          )}

          {(validFrom || validUntil) && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="text-xs text-blue-800 space-y-0.5">
                {validFrom && <div>Available from: {new Date(validFrom).toLocaleString()}</div>}
                {validUntil && <div>Available until: {new Date(validUntil).toLocaleString()}</div>}
              </div>
            </div>
          )}

          {/* Status messages */}
          {notYetAvailable && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              This assessment is not yet available. Please check back after {validFrom.toLocaleString()}.
            </div>
          )}

          {expired && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              This assessment has expired.
            </div>
          )}

          {alreadySubmitted && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              You have already submitted this assessment.
            </div>
          )}

          {/* Start button */}
          <button
            onClick={onStart}
            disabled={!canStart || isLoading}
            className={clsx(
              'w-full py-3 rounded-xl text-base font-semibold transition-colors',
              canStart && !isLoading
                ? 'bg-sigma-700 text-white hover:bg-sigma-600 shadow-md'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            {isLoading ? 'Starting...' : alreadySubmitted ? 'Already Submitted' : 'Start Assessment'}
          </button>

          {assessment.timeLimit && canStart && (
            <p className="text-xs text-center text-gray-400">
              Once you start, the timer cannot be paused.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentDocStartScreen;
